<template>
    <div class="flex flex-col dark:text-white">
        <div class="mb-4 flex items-center justify-between">
            <div>
                <h1 class="text-xl font-semibold text-gray-900 dark:text-white">Operações do Checkout</h1>
                <p class="text-sm text-gray-500 dark:text-gray-400">Visão operacional em tempo real das submissões do checkout.</p>
            </div>
            <button
                @click="refreshAll"
                :disabled="anyLoading"
                type="button"
                :class="{ 'cursor-not-allowed opacity-60': anyLoading }"
                class="inline-flex items-center justify-center rounded-md bg-indigo-500 px-3 py-1.5 text-sm font-semibold text-white shadow transition duration-150 ease-in-out hover:bg-indigo-400"
            >
                <font-awesome-icon :icon="['fas', 'rotate']" class="mr-2" />
                Atualizar
            </button>
        </div>

        <div class="mb-4 rounded-lg bg-slate-50 p-4 dark:bg-slate-800/25">
            <div class="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-6">
                <div class="lg:col-span-2">
                    <label class="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">Período</label>
                    <litepie-datepicker
                        v-model="filters.date"
                        :formatter="{ date: 'DD/MM/YYYY', month: 'MMM' }"
                        :shortcuts="false"
                    />
                </div>
                <div>
                    <label class="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">Status</label>
                    <select
                        v-model="filters.passed"
                        class="block w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    >
                        <option value="">Todos</option>
                        <option value="1">Aprovadas</option>
                        <option value="0">Reprovadas</option>
                    </select>
                </div>
                <div>
                    <label class="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">Reprova</label>
                    <input
                        v-model="filters.failed_check"
                        type="text"
                        placeholder="Ex.: cpf_blacklisted"
                        class="block w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    />
                </div>
                <div>
                    <label class="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">Código da oferta</label>
                    <input
                        v-model="filters.offer_code"
                        type="text"
                        class="block w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    />
                </div>
                <div>
                    <label class="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">Produtor (ID)</label>
                    <input
                        v-model="filters.producer_id"
                        type="text"
                        class="block w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    />
                </div>
                <div class="lg:col-span-4">
                    <label class="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">Buscar (email, CPF, IP, visitor)</label>
                    <input
                        v-model="filters.text"
                        type="text"
                        @keyup.enter="applyFilters"
                        class="block w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    />
                </div>
                <div class="flex items-end gap-2 lg:col-span-2">
                    <button
                        @click="applyFilters"
                        :disabled="anyLoading"
                        class="inline-flex items-center justify-center rounded-md bg-indigo-500 px-3 py-2 text-sm font-semibold text-white shadow hover:bg-indigo-400 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                        Aplicar
                    </button>
                    <button
                        @click="resetFilters"
                        :disabled="anyLoading"
                        class="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-600 dark:bg-slate-700 dark:text-white"
                    >
                        Limpar
                    </button>
                </div>
            </div>
        </div>

        <div class="mb-4 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
            <metric-card
                title="Total de submissões"
                :value="formatNumber(summary.total_submissions)"
                subtitle="No período filtrado"
            />
            <metric-card
                title="Taxa de erro"
                :value="`${formatNumber(summary.error_rate)}%`"
                subtitle="Submissões reprovadas / total"
                :trend="summary.error_rate >= 25 ? 'danger' : (summary.error_rate >= 10 ? 'warning' : null)"
            />
            <metric-card
                title="Reprova mais frequente"
                :value="summary.top_failed_check ? summary.top_failed_check.key : '—'"
                :subtitle="summary.top_failed_check ? `${summary.top_failed_check.count} ocorrências` : 'Sem reprovas'"
            />
            <metric-card
                title="Últimas 24h"
                :value="formatNumber(summary.last_24h ? summary.last_24h.count : 0)"
                :subtitle="last24hSubtitle"
                :trend="summary.last_24h && summary.last_24h.delta_percent > 0 ? 'warning' : null"
            />
        </div>

        <div class="mb-4 rounded-lg bg-slate-50 p-4 dark:bg-slate-800/25">
            <h2 class="mb-3 text-sm font-semibold text-gray-900 dark:text-white">Reprovações por categoria (por dia)</h2>
            <chart-skeleton v-if="trendLoading" />
            <div v-else-if="!trendChartOptions.series.length" class="py-10 text-center text-sm text-gray-500 dark:text-gray-400">
                Sem dados para o período selecionado.
            </div>
            <highcharts v-else class="hc" :options="trendChartOptions" />
        </div>

        <table-component
            :loading="submissionsLoading"
            :pagination="paginationInfo"
            :has-next-page="paginationInfo.has_next_page"
            :is-empty="!submissions.length"
            :headers="tableHeaders"
            @next-page="nextPage"
            @prev-page="prevPage"
        >
            <tr
                v-for="item in submissions"
                :key="item.id"
                class="cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700/40"
                @click="openDetail(item)"
            >
                <td class="table-cell-sm whitespace-nowrap">{{ formatDateTime(item.created_at) }}</td>
                <td class="table-cell-sm">{{ item.email || '—' }}</td>
                <td class="table-cell-sm">{{ item.cpf || '—' }}</td>
                <td class="table-cell-sm">{{ item.ip || '—' }}</td>
                <td class="table-cell-sm">
                    <span
                        class="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium"
                        :class="item.passed ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'"
                    >
                        {{ item.passed ? 'Aprovada' : 'Reprovada' }}
                    </span>
                </td>
                <td class="table-cell-sm">{{ item.failed_check || '—' }}</td>
                <td class="table-cell-sm">{{ productLabel(item) }}</td>
            </tr>
        </table-component>

        <submission-detail-drawer
            v-if="drawerVisitorId"
            :visitor-id="drawerVisitorId"
            @close="closeDetail"
        />
    </div>
</template>

<script>
import axios from "axios";
import { useToast } from "vue-toastification";
import MetricCard from "../../components/MetricCard.vue";
import ChartSkeleton from "../../components/layouts/ChartSkeleton.vue";
import TableComponent from "../../components/layouts/table/TableComponent.vue";
import SubmissionDetailDrawer from "./components/SubmissionDetailDrawer.vue";

const defaultFilters = () => ({
    date: { startDate: "", endDate: "" },
    passed: "",
    failed_check: "",
    offer_code: "",
    producer_id: "",
    text: "",
});

export default {
    components: { MetricCard, ChartSkeleton, TableComponent, SubmissionDetailDrawer },
    setup() {
        return { toast: useToast() };
    },
    data() {
        return {
            filters: defaultFilters(),
            summary: {
                total_submissions: 0,
                failed_count: 0,
                error_rate: 0,
                top_failed_check: null,
                last_24h: { count: 0, delta_percent: 0 },
            },
            summaryLoading: false,
            trendSeries: [],
            trendLoading: false,
            submissions: [],
            submissionsLoading: false,
            page: 1,
            perPage: 25,
            hasNextPage: false,
            drawerVisitorId: null,
        };
    },
    computed: {
        anyLoading() {
            return this.summaryLoading || this.trendLoading || this.submissionsLoading;
        },
        tableHeaders() {
            return ["Data", "E-mail", "CPF", "IP", "Status", "Motivo", "Produto"];
        },
        paginationInfo() {
            return {
                current_page: this.page,
                per_page: this.perPage,
                has_next_page: this.hasNextPage,
            };
        },
        last24hSubtitle() {
            const delta = this.summary.last_24h ? this.summary.last_24h.delta_percent : 0;
            if (!delta) {
                return "Sem variação vs 24h anteriores";
            }
            const sign = delta > 0 ? "+" : "";
            return `${sign}${delta}% vs 24h anteriores`;
        },
        trendChartOptions() {
            return {
                chart: { type: "column" },
                title: { text: null },
                credits: { enabled: false },
                legend: { enabled: true },
                xAxis: { type: "datetime", title: { text: null } },
                yAxis: { allowDecimals: false, title: { text: null }, min: 0 },
                tooltip: { shared: true, xDateFormat: "%d/%m/%Y" },
                plotOptions: { column: { stacking: "normal", borderWidth: 0 } },
                series: this.trendSeries.map((s) => ({
                    name: s.name,
                    data: s.data,
                })),
            };
        },
    },
    mounted() {
        this.fetchAll();
    },
    methods: {
        applyFilters() {
            this.page = 1;
            this.fetchAll();
        },
        resetFilters() {
            this.filters = defaultFilters();
            this.page = 1;
            this.fetchAll();
        },
        refreshAll() {
            this.fetchAll();
        },
        fetchAll() {
            this.fetchSummary();
            this.fetchTrend();
            this.fetchSubmissions();
        },
        queryParams() {
            const params = {};
            if (this.filters.date && this.filters.date.startDate) {
                params.date_from = this.filters.date.startDate;
            }
            if (this.filters.date && this.filters.date.endDate) {
                params.date_to = this.filters.date.endDate;
            }
            if (this.filters.passed !== "") {
                params.passed = this.filters.passed;
            }
            if (this.filters.failed_check) {
                params.failed_check = this.filters.failed_check;
            }
            if (this.filters.offer_code) {
                params.offer_code = this.filters.offer_code;
            }
            if (this.filters.producer_id) {
                params.producer_id = this.filters.producer_id;
            }
            if (this.filters.text) {
                params.text = this.filters.text;
            }
            return params;
        },
        fetchSummary() {
            this.summaryLoading = true;
            axios
                .get("/api/operations/checkout/summary", { params: this.queryParams() })
                .then(({ data }) => {
                    this.summary = data.data;
                })
                .catch(() => {
                    this.toast.error("Não foi possível carregar o resumo.");
                })
                .finally(() => {
                    this.summaryLoading = false;
                });
        },
        fetchTrend() {
            this.trendLoading = true;
            axios
                .get("/api/operations/checkout/error-trend", { params: this.queryParams() })
                .then(({ data }) => {
                    this.trendSeries = data.data.series || [];
                })
                .catch(() => {
                    this.toast.error("Não foi possível carregar o gráfico.");
                })
                .finally(() => {
                    this.trendLoading = false;
                });
        },
        fetchSubmissions() {
            this.submissionsLoading = true;
            axios
                .get("/api/operations/checkout/recent-submissions", {
                    params: { ...this.queryParams(), page: this.page, per_page: this.perPage },
                })
                .then(({ data }) => {
                    this.submissions = data.data.items || [];
                    this.hasNextPage = !!data.data.has_next_page;
                })
                .catch(() => {
                    this.toast.error("Não foi possível carregar as submissões.");
                })
                .finally(() => {
                    this.submissionsLoading = false;
                });
        },
        nextPage() {
            if (!this.hasNextPage) {
                return;
            }
            this.page += 1;
            this.fetchSubmissions();
        },
        prevPage() {
            if (this.page <= 1) {
                return;
            }
            this.page -= 1;
            this.fetchSubmissions();
        },
        openDetail(item) {
            if (!item || !item.visitorId) {
                this.toast.error("Submissão sem visitor associado.");
                return;
            }
            this.drawerVisitorId = item.visitorId;
        },
        closeDetail() {
            this.drawerVisitorId = null;
        },
        productLabel(item) {
            if (!item.product) {
                return "—";
            }
            if (typeof item.product === "string") {
                return item.product;
            }
            return item.product.name || item.product.id || "—";
        },
        formatNumber(value) {
            if (value === null || value === undefined) {
                return "0";
            }
            return new Intl.NumberFormat("pt-BR").format(value);
        },
        formatDateTime(value) {
            if (!value) {
                return "—";
            }
            const d = new Date(value);
            if (Number.isNaN(d.getTime())) {
                return value;
            }
            return d.toLocaleString("pt-BR");
        },
    },
};
</script>
